import { useRef, useState } from 'react';
import { Upload, FileText, Image, FileCode, File, Trash2, Eye } from 'lucide-react';
import type { Attachment } from '../../types';
import { useApp } from '../../context/AppContext';
import { formatFileSize, formatRelativeDate, isPreviewableFile } from '../../utils/helpers';
import { uploadFile } from '../../services/api';
import FilePreviewModal from '../common/FilePreviewModal';
import ConfirmDialog from '../common/ConfirmDialog';

interface TaskAttachmentsProps {
  taskId: string;
  attachments: Attachment[];
}

function getFileIcon(type: string) {
  if (type.startsWith('image/')) return Image;
  if (type.includes('pdf')) return FileText;
  if (type.includes('code') || type.includes('javascript') || type.includes('typescript')) return FileCode;
  return File;
}

export default function TaskAttachments({ taskId, attachments }: TaskAttachmentsProps) {
  const { apiAddAttachment, apiRemoveAttachment, getUserById } = useApp();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewFile, setPreviewFile] = useState<Attachment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Attachment | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      try {
        const uploaded = await uploadFile(file);
        await apiAddAttachment(taskId, {
          name: file.name,
          size: file.size,
          type: file.type,
          url: uploaded.path,
        });
      } catch {
        // Fallback to local blob URL if upload fails (e.g., no Azure config)
        await apiAddAttachment(taskId, {
          name: file.name,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file),
        });
      }
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-700">
          Anexos ({attachments.length})
        </h4>
        <label
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 cursor-pointer transition-colors"
          role="button"
          tabIndex={0}
        >
          <Upload size={14} />
          Enviar
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileChange}
            className="sr-only"
            aria-label="Enviar arquivo"
          />
        </label>
      </div>

      <div className="space-y-2">
        {attachments.map((attachment) => {
          const Icon = getFileIcon(attachment.type);
          const uploader = getUserById(attachment.uploadedBy);

          return (
            <div
              key={attachment.id}
              className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors group"
            >
              <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                <Icon size={18} className="text-brand-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-700 truncate">{attachment.name}</p>
                <p className="text-xs text-slate-400">
                  {formatFileSize(attachment.size)} &middot; {uploader?.name || 'Unknown'} &middot; {formatRelativeDate(attachment.uploadedAt)}
                </p>
              </div>
              {isPreviewableFile(attachment.type) && (
                <button
                  onClick={() => setPreviewFile(attachment)}
                  className="p-1.5 rounded-lg text-slate-300 hover:text-brand-500 hover:bg-brand-50 transition-all"
                  aria-label={`Visualizar ${attachment.name}`}
                >
                  <Eye size={14} />
                </button>
              )}
              <button
                onClick={() => setDeleteTarget(attachment)}
                className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all"
                aria-label={`Remover ${attachment.name}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}

        {attachments.length === 0 && (
          <div className="text-center py-6 rounded-xl border-2 border-dashed border-slate-200">
            <Upload size={24} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">Nenhum anexo ainda</p>
            <p className="text-xs text-slate-300 mt-1">Clique em enviar para adicionar arquivos</p>
          </div>
        )}
      </div>

      {previewFile && (
        <FilePreviewModal
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          fileName={previewFile.name}
          fileType={previewFile.type}
          fileUrl={previewFile.url}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Remover anexo"
        message="Tem certeza que deseja remover este anexo?"
        confirmLabel="Remover"
        onConfirm={async () => {
          if (deleteTarget) {
            await apiRemoveAttachment(taskId, deleteTarget.id);
          }
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
